import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Download, Trash2, Phone, CheckCircle, PhoneOff,
    Voicemail, XCircle, User, Wallet, FileText, ClipboardList,
    CalendarDays, IdCard, Clock, Save, MessageSquareText, Paperclip,
    Database, History, UploadCloud, ArrowLeft,
} from 'lucide-react';
import patientCallingService, {
    PatientServiceLog,
    DispatchLog,
    PatientCallAttachment
} from '../../../services/patientCalling.service';
import Loading from '../../Common/Loading';
import HeaderCrumbs from '../../Common/HeaderCrumbs';
import CommonDropdown from '../../Common/CommonDropdown';
import ConfirmModal from '../../Common/ConfirmModal';
import Toast from '../../Common/Toast';
import AttachmentUploadModal from './AttachmentUploadModal';
import styles from './PatientDetail.module.css';

const TABS = ['Overview', 'Visit Details', 'Call Log', 'Attachments', 'Extra Fields'];

const STATUS_COLORS: Record<string, string> = {
    PENDING: '#6366f1', SCHEDULED: '#3b82f6', IN_PROGRESS: '#f59e0b',
    COMPLETED: '#10b981', NO_ANSWER: '#f97316', VOICEMAIL: '#a855f7', FAILED: '#ef4444',
};
const STATUS_BG: Record<string, string> = {
    PENDING: '#e0e7ff', SCHEDULED: '#dbeafe', IN_PROGRESS: '#fef3c7',
    COMPLETED: '#dcfce7', NO_ANSWER: '#ffedd5', VOICEMAIL: '#ede9fe', FAILED: '#fee2e2',
};

const OUTCOME_ICONS: Record<string, React.ReactNode> = {
    COMPLETED: <CheckCircle size={15} color="#10b981" />,
    NO_ANSWER: <PhoneOff size={15} color="#f97316" />,
    VOICEMAIL: <Voicemail size={15} color="#a855f7" />,
    FAILED: <XCircle size={15} color="#ef4444" />,
};

const CALL_STATUS_OPTIONS = ['PENDING', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'NO_ANSWER', 'VOICEMAIL', 'FAILED'];

const getInitials = (name: string): string => {
    const parts = name.replace(',', '').split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const money = (n: number | null | undefined) =>
    n != null ? `$${n.toFixed(2)}` : undefined;

const PatientDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const locationState = (location.state as { batchId?: string; batchName?: string } | null) ?? {};

    const handleBack = () => {
    navigate('/patient-calling');
};

    const [record, setRecord] = useState<PatientServiceLog | null>(null);
    const [logs, setLogs] = useState<DispatchLog[]>([]);
    const [attachments, setAttachments] = useState<PatientCallAttachment[]>([]);
    const [siblingLogs, setSiblingLogs] = useState<PatientServiceLog[]>([]);
    const [loadingSiblings, setLoadingSiblings] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusUpdate, setStatusUpdate] = useState('');
    const [notes, setNotes] = useState('');
    const [savingStatus, setSavingStatus] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<PatientCallAttachment | null>(null);
    const [deletingAttachment, setDeletingAttachment] = useState(false);

    const [toast, setToast] = useState<{
        message: string;
        type: 'success' | 'error' | 'info' | 'warning';
    } | null>(null);

    useEffect(() => { loadAll(); }, [id]);

    const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    try {
        const [r, logRes, atts] = await Promise.all([
            patientCallingService.getServiceLog(id),

            // ✅ FIXED
            patientCallingService.listDispatchLogs({ service_log_id: id }),

            patientCallingService.listAttachments(id),
        ]);

        setRecord(r);
        setStatusUpdate(r.call_status || '');
        setNotes(r.call_notes || '');
        setLogs(logRes.items);
        setAttachments(atts);

        // Sibling service logs for the same patient
        if (r.patient_uuid) {
            setLoadingSiblings(true);
            try {
                const sibs = await patientCallingService.getAllRecords({
                    patient_uuid: r.patient_uuid,
                    page: 1,
                    page_size: 100,
                });
                setSiblingLogs(sibs.data ?? []);
            } catch (err) {
                console.error('Failed to load sibling logs', err);
                setSiblingLogs([]);
            } finally {
                setLoadingSiblings(false);
            }
        }
    } catch (e) {
        console.error(e);
    } finally {
        setLoading(false);
    }
};

    const handleSaveStatus = async () => {
        if (!id) return;
        setSavingStatus(true);
        try {
            const updated = await patientCallingService.updateServiceLog(id, {
                call_status_code: statusUpdate,
                call_notes: notes,
            });
            setRecord(updated);
            setToast({ type: 'success', message: 'Status updated' });
        } catch (e: any) {
            setToast({ type: 'error', message: e?.message || 'Failed to update status' });
        } finally {
            setSavingStatus(false);
        }
    };

    const handleUploadedAttachments = (newAttachments: PatientCallAttachment[]) => {
        setAttachments(prev => [...newAttachments, ...prev]);
    };

    const handleDownload = async (attachmentId: string) => {
        try {
            const { url } = await patientCallingService.getAttachmentDownloadUrl(attachmentId);
            window.open(url, '_blank');
        } catch (e: any) {
            setToast({ type: 'error', message: e?.message || 'Download failed' });
        }
    };

    const handleDeleteAttachment = (att: PatientCallAttachment) => {
        setAttachmentToDelete(att);
    };

    const confirmDeleteAttachment = async () => {
        if (!attachmentToDelete) return;
        setDeletingAttachment(true);
        try {
            await patientCallingService.deleteAttachment(attachmentToDelete.id);
            setAttachments(prev => prev.filter(a => a.id !== attachmentToDelete.id));
            setAttachmentToDelete(null);
        } catch (e: any) {
            setToast({ type: 'error', message: e?.message || 'Delete failed' });
        } finally {
            setDeletingAttachment(false);
        }
    };

    if (loading) return <div className={styles.page}><Loading message="Loading patient record..." /></div>;
    if (!record) return <div className={styles.page}><div className={styles.loading}>Record not found</div></div>;

    const isPayment = record.call_type === 'payment_reminder';
    const statusKey = record.call_status || '';
    const statusFg = STATUS_COLORS[statusKey] || '#6b7280';
    const statusBg = STATUS_BG[statusKey] || '#f3f4f6';

    // Count of sibling service logs in PENDING state (treat missing status as PENDING)
    const pendingCount = siblingLogs.filter(
        l => (l.call_status || 'PENDING') === 'PENDING'
    ).length;
    const pendingFg = STATUS_COLORS.PENDING;
    const pendingBg = STATUS_BG.PENDING;

    return (
        <div className={styles.page}>
            {toast && (
                <div className={styles.toastWrap}>
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                </div>
            )}
            <HeaderCrumbs>
                {locationState.batchId ? (
                    <Link to={`/patient-calling/batches/${locationState.batchId}`}>
                        {locationState.batchName || 'Batch'}
                    </Link>
                ) : null}
                <span>{record.patient_name}</span>
            </HeaderCrumbs>

            {/* Hero Card (slim, single row) */}
            <div
                className={styles.patientHero}
                style={{ ['--hero-color' as any]: statusFg }}
            >
                <div className={styles.patientHeroBody}>
                    <div className={styles.patientHeroLeft}>
                        <button
                            type="button"
                            className={styles.backButton}
                            onClick={handleBack}
                            title={locationState.batchName ? `Back to ${locationState.batchName}` : 'Back to Patient Calling'}
                        >
                            <ArrowLeft size={16} />
                        </button>
                        <div className={styles.patientAvatar} style={{ ['--hero-color' as any]: statusFg }}>
                            {getInitials(record.patient_name)}
                        </div>
                        <div className={styles.patientHeroInfo}>
                            <h1 className={styles.patientName}>{record.patient_name}</h1>
                            <div className={styles.metaChipRow}>
                                {record.patient_id && (
                                    <span className={styles.metaChip}>
                                        <IdCard size={11} />
                                        {record.patient_id}
                                    </span>
                                )}
                                {record.dob && (
                                    <span className={styles.metaChip}>
                                        <CalendarDays size={11} />
                                        {record.dob.split(' ')[0]}
                                    </span>
                                )}
                                {record.phone_number && (
                                    <a href={`tel:${record.phone_number}`} className={`${styles.metaChip} ${styles.metaChipLink}`}>
                                        <Phone size={11} />
                                        {record.phone_number}
                                    </a>
                                )}
                                {record.last_called_at && (
                                    <span className={styles.lastCalledText}>
                                        <Clock size={11} />
                                        Last called {new Date(record.last_called_at).toLocaleString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.patientHeroRight}>
                        {siblingLogs.length > 0 ? (
                            <span
                                className={styles.statusPill}
                                style={{ background: pendingBg, color: pendingFg, borderColor: pendingFg + '33' }}
                                title={`${pendingCount} of ${siblingLogs.length} service log${siblingLogs.length !== 1 ? 's' : ''} pending`}
                            >
                                <span className={styles.statusPillDot} style={{ background: pendingFg }} />
                                {pendingCount} Pending
                                <span className={styles.statusPillMeta}>/ {siblingLogs.length}</span>
                            </span>
                        ) : record.call_status && (
                            <span
                                className={styles.statusPill}
                                style={{ background: statusBg, color: statusFg, borderColor: statusFg + '33' }}
                            >
                                <span className={styles.statusPillDot} style={{ background: statusFg }} />
                                {record.call_status.replace('_', ' ')}
                            </span>
                        )}
                        {/* {record.phone_number && (
                            <a href={`tel:${record.phone_number}`} className={`${styles.actionPillBtn} ${styles.actionPillBtnPrimary}`}>
                                <Phone size={13} /> Call Patient
                            </a>
                        )} */}
                    </div>
                </div>
            </div>

            {/* Compact Metric Strip */}
            {isPayment && (record.total_balance != null || record.current_due != null || record.charge != null) && (
                <div className={styles.metricStrip}>
                    {record.total_balance != null && (
                        <MetricTile label="Total Balance" value={money(record.total_balance)!} color="#0e7490" featured />
                    )}
                    {record.current_due != null && (
                        <MetricTile label="Current Due" value={money(record.current_due)!} color="#f59e0b" />
                    )}
                    {record.charge != null && (
                        <MetricTile label="Charge" value={money(record.charge)!} color="#6366f1" />
                    )}
                    {record.over_90_days != null && record.over_90_days > 0 && (
                        <MetricTile label="Over 90 Days" value={money(record.over_90_days)!} color="#ef4444" />
                    )}
                </div>
            )}
            {!isPayment && (record.total_balance != null || record.billed_amount != null) && (
                <div className={styles.metricStrip}>
                    {record.total_balance != null && (
                        <MetricTile label="Balance" value={money(record.total_balance)!} color="#0e7490" featured />
                    )}
                    {record.billed_amount != null && (
                        <MetricTile label="Billed Amount" value={money(record.billed_amount)!} color="#6366f1" />
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className={styles.tabBar}>
                {TABS.map((tab, i) => (
                    <button key={tab} className={`${styles.tab} ${activeTab === i ? styles.tabActive : ''}`} onClick={() => setActiveTab(i)}>
                        {tab}
                        {tab === 'Attachments' && attachments.length > 0 && <span className={styles.tabBadge}>{attachments.length}</span>}
                        {tab === 'Call Log' && logs.length > 0 && <span className={styles.tabBadge}>{logs.length}</span>}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className={styles.tabContent}>
                {/* Overview */}
                {activeTab === 0 && (
                    <>
                    <div className={styles.overviewGrid}>
                        {/* COL 1: Patient Info */}
                        <div className={styles.overviewCol}>
                            <InfoCard title="Patient Info" icon={<User size={13} />} color="#3b82f6">
                                <InfoRow label="Name" value={record.patient_name} />
                                <InfoRow label="DOB" value={record.dob} />
                                <InfoRow label="Phone" value={record.phone_number} />
                                <InfoRow label="Address" value={[record.address_line_1, record.address_line_2].filter(Boolean).join(', ')} />
                                <InfoRow label="Insurance" value={record.insurance_name} />
                                <InfoRow label="Insurance ID" value={record.insurance_id} />
                                {record.secondary_insurance && <InfoRow label="Secondary Ins." value={record.secondary_insurance} />}
                                {record.secondary_insurance_id && <InfoRow label="Secondary ID" value={record.secondary_insurance_id} />}
                            </InfoCard>
                        </div>

                        {/* COL 2: Provider + Visit/Statement details */}
                        <div className={styles.overviewCol}>
                            {isPayment ? (
                                <InfoCard title="Statement Details" icon={<Wallet size={13} />} color="#0e7490">
                                    <InfoRow label="DOS" value={record.dos} />
                                    <InfoRow label="Statement Date" value={record.statement_date} />
                                    <InfoRow label="Charge" value={money(record.charge)} />
                                    <InfoRow label="Primary Payment" value={money(record.primary_payment)} />
                                    <InfoRow label="Secondary Payment" value={money(record.secondary_payment)} />
                                    <InfoRow label="Patient Payment" value={money(record.patient_payment)} />
                                    <InfoRow label="Adjustment" value={money(record.adjustment)} />
                                    {(record.facility_name || record.check_payable_to) && (
                                        <div className={styles.infoSubsection}>
                                            <div className={styles.infoSubsectionLabel}>Provider</div>
                                            {record.facility_name && record.check_payable_to && record.facility_name !== record.check_payable_to ? (
                                                <>
                                                    <InfoRow label="Facility" value={record.facility_name} />
                                                    <InfoRow label="Check Payable To" value={record.check_payable_to} />
                                                </>
                                            ) : (
                                                <InfoRow label="Practice" value={record.check_payable_to || record.facility_name} />
                                            )}
                                        </div>
                                    )}
                                </InfoCard>
                            ) : (
                                <InfoCard title="Visit & Claim" icon={<FileText size={13} />} color="#6366f1">
                                    <InfoRow label="DOS" value={record.dos} />
                                    <InfoRow label="Visit ID" value={record.visit_id} />
                                    <InfoRow label="CPT Code" value={record.cpt_code} />
                                    <InfoRow label="ICD Code" value={record.icd_code} />
                                    <InfoRow label="Billed Amount" value={money(record.billed_amount)} />
                                    <InfoRow label="Balance" value={money(record.total_balance)} highlight />
                                    {(record.facility_name || record.check_payable_to || record.rendering_provider || record.billing_provider) && (
                                        <div className={styles.infoSubsection}>
                                            <div className={styles.infoSubsectionLabel}>Provider</div>
                                            <InfoRow label="Practice" value={record.check_payable_to || record.facility_name} />
                                            <InfoRow label="Rendering" value={record.rendering_provider} />
                                            <InfoRow label="Billing" value={record.billing_provider} />
                                        </div>
                                    )}
                                </InfoCard>
                            )}
                        </div>

                        {/* COL 3: Aging breakdown (payment) OR AR Workflow (info_gathering) */}
                        <div className={styles.overviewCol}>
                            {isPayment ? (
                                <InfoCard title="Aging & Total" icon={<Wallet size={13} />} color="#f59e0b">
                                    <InfoRow label="Current Due" value={money(record.current_due)} />
                                    <InfoRow label="Over 30 Days" value={money(record.over_30_days)} />
                                    <InfoRow label="Over 60 Days" value={money(record.over_60_days)} />
                                    <InfoRow label="Over 90 Days" value={money(record.over_90_days)} />
                                    <InfoRow label="Reference" value={record.reference} />
                                    <InfoRow label="Total Balance" value={money(record.total_balance)} highlight />
                                </InfoCard>
                            ) : (
                                <InfoCard title="AR Workflow" icon={<ClipboardList size={13} />} color="#a855f7">
                                    {(record.reason_for_call || record.ar_status || record.ar_assigned_to) && (
                                        <div className={styles.arPillRow}>
                                            {record.reason_for_call && <ArPill tone="violet" label="Reason for Call" value={record.reason_for_call} />}
                                            {record.ar_status && <ArPill tone="amber" label="AR Status" value={record.ar_status} />}
                                            {record.ar_assigned_to && <ArPill tone="blue" label="Assigned To" value={record.ar_assigned_to} />}
                                        </div>
                                    )}
                                    {record.ar_comments && (
                                        <div className={styles.arCommentsBox}>
                                            <div className={styles.arCommentsHeader}>
                                                <MessageSquareText size={12} />
                                                AR Comments
                                            </div>
                                            <div className={styles.arCommentsText}>{record.ar_comments}</div>
                                        </div>
                                    )}
                                    {!record.reason_for_call && !record.ar_status && !record.ar_assigned_to && !record.ar_comments && (
                                        <div className={styles.emptyState}><p style={{ margin: 0 }}>No AR workflow data</p></div>
                                    )}
                                </InfoCard>
                            )}
                        </div>
                    </div>

                    {/* All service logs for this patient */}
                    <div className={styles.serviceLogsSection}>
                        <div className={styles.tabSectionHeader}>
                            <h3 className={styles.tabSectionTitle}>
                                <Database size={13} />
                                All Service Logs
                                {siblingLogs.length > 0 && (
                                    <span className={styles.tabSectionCount}>{siblingLogs.length}</span>
                                )}
                            </h3>
                        </div>

                        {loadingSiblings ? (
                            <div className={styles.serviceLogsLoading}>Loading service logs…</div>
                        ) : siblingLogs.length === 0 ? (
                            <EmptyState
                                icon={<Database size={22} />}
                                title="No service logs"
                                text="No other service logs exist for this patient."
                            />
                        ) : (
                            <div className={styles.dataTableWrap}>
                                <div className={styles.dataTableScroll}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th>DOS</th>
                                                <th>Call Type</th>
                                                <th>Insurance</th>
                                                <th>Status</th>
                                                <th style={{ textAlign: 'right' }}>Total Balance</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {siblingLogs.map(log => {
                                                const isCurrent = log.id === record.id;
                                                const sFg = STATUS_COLORS[log.call_status || ''] || '#6b7280';
                                                const sBg = STATUS_BG[log.call_status || ''] || '#f3f4f6';
                                                return (
                                                    <tr
                                                        key={log.id}
                                                        className={`${styles.serviceLogRow} ${isCurrent ? styles.serviceLogRowCurrent : ''}`}
                                                        onClick={() => !isCurrent && navigate(`/patient-calling/records/${log.id}`)}
                                                        title={isCurrent ? 'Currently viewing this record' : 'Open this service log'}
                                                    >
                                                        <td>{log.dos || '—'}</td>
                                                        <td>
                                                            {log.call_type === 'payment_reminder'
                                                                ? 'Payment Reminder'
                                                                : log.call_type === 'patient_info_gathering'
                                                                    ? 'Patient Info Gathering'
                                                                    : '—'}
                                                        </td>
                                                        <td>{log.insurance_name || '—'}</td>
                                                        <td>
                                                            <span
                                                                className={styles.statusPill}
                                                                style={{ background: sBg, color: sFg, borderColor: sFg + '33' }}
                                                            >
                                                                <span className={styles.statusPillDot} style={{ background: sFg }} />
                                                                {(log.call_status || 'PENDING').replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            {money(log.total_balance) || '—'}
                                                        </td>
                                                        <td>
                                                            {log.created_at
                                                                ? new Date(log.created_at).toLocaleDateString()
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                    </>
                )}

                {/* Visit Details */}
                {activeTab === 1 && (
                    <>
                        <div className={styles.tabSectionHeader}>
                            <h3 className={styles.tabSectionTitle}>
                                <FileText size={13} />
                                Visit History
                                {record.visit_details && record.visit_details.length > 0 && (
                                    <span className={styles.tabSectionCount}>{record.visit_details.length}</span>
                                )}
                            </h3>
                        </div>
                        {!record.visit_details || record.visit_details.length === 0 ? (
                            <EmptyState
                                icon={<FileText size={22} />}
                                title="No visit details"
                                text="Visit history wasn't included in the import file for this patient."
                            />
                        ) : (
                            <div className={styles.dataTableWrap}>
                                <div className={styles.dataTableScroll}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                {Object.keys(record.visit_details[0]).map(k => <th key={k}>{k}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.visit_details.map((row: any, i: number) => (
                                                <tr key={i}>
                                                    {Object.values(row).map((v: any, j: number) => <td key={j}>{String(v ?? '—')}</td>)}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Call Log */}
                {activeTab === 2 && (
                    <>
                        {/* Quick Status Update (compact) */}
                        <div className={styles.quickUpdateCard}>
                            <CommonDropdown
                                value={statusUpdate}
                                onChange={(v) => setStatusUpdate(v || '')}
                                options={CALL_STATUS_OPTIONS.map(s => ({ value: s, label: s.replace('_', ' ') }))}
                                placeholder="— Update Status —"
                                size="md"
                                isSearchable
                            />
                            <input
                                type="text"
                                className={styles.input}
                                placeholder="Agent notes (optional)..."
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                            />
                            <button className={`${styles.actionPillBtn} ${styles.actionPillBtnPrimary}`} onClick={handleSaveStatus} disabled={savingStatus}>
                                <Save size={13} /> {savingStatus ? 'Saving...' : 'Save Update'}
                            </button>
                        </div>

                        <div className={styles.tabSectionHeader}>
                            <h3 className={styles.tabSectionTitle}>
                                <History size={13} />
                                Dispatch Timeline
                                {logs.length > 0 && <span className={styles.tabSectionCount}>{logs.length}</span>}
                            </h3>
                        </div>
                        {logs.length === 0 ? (
                            <EmptyState
                                icon={<History size={22} />}
                                title="No call attempts yet"
                                text="Dispatched calls will appear here in chronological order with their outcome and response."
                            />
                        ) : (
                            <div className={styles.timeline}>
                                {logs.map(log => {
                                    const ok = !!(log.http_status && log.http_status < 300);
                                    const hasStatus = log.http_status != null;
                                    const markerBg = ok ? '#dcfce7' : (hasStatus ? '#fee2e2' : '#f1f5f9');
                                    const markerFg = ok ? '#10b981' : (hasStatus ? '#ef4444' : '#94a3b8');
                                    const httpClass = ok ? styles.httpBadgeOk : (hasStatus ? styles.httpBadgeErr : styles.httpBadgeNeutral);
                                    const detailContent = log.error || (log.response_body ? JSON.stringify(log.response_body, null, 2) : null);
                                    return (
                                        <div className={styles.timelineItem} key={log.id}>
                                            <div className={styles.timelineMarkerWrap}>
                                                <div
                                                    className={styles.timelineMarker}
                                                    style={{ ['--marker-bg' as any]: markerBg, ['--marker-fg' as any]: markerFg }}
                                                >
                                                    {ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                                </div>
                                                <div className={styles.timelineLine} />
                                            </div>
                                            <div className={styles.timelineContent}>
                                                <div className={styles.timelineRow}>
                                                    <span className={styles.timelineTime}>
                                                        {log.dispatched_at ? new Date(log.dispatched_at).toLocaleString() : '—'}
                                                    </span>
                                                    <div className={styles.timelineRight}>
                                                        <span className={`${styles.httpBadge} ${httpClass}`}>
                                                            HTTP {log.http_status ?? '—'}
                                                        </span>
                                                        {record.call_status && (
                                                            <span className={styles.timelineOutcome}>
                                                                {OUTCOME_ICONS[record.call_status] || null}
                                                                {record.call_status.replace('_', ' ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {detailContent && (
                                                    <div className={`${styles.timelineDetail} ${log.error ? styles.timelineDetailErr : ''}`}>
                                                        {detailContent}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Attachments */}
                {activeTab === 3 && (
                    <>
                        <div className={styles.tabSectionHeader}>
                            <h3 className={styles.tabSectionTitle}>
                                <Paperclip size={13} />
                                Files
                                {attachments.length > 0 && <span className={styles.tabSectionCount}>{attachments.length}</span>}
                            </h3>
                            <button
                                className={`${styles.actionPillBtn} ${styles.actionPillBtnPrimary}`}
                                onClick={() => setShowUploadModal(true)}
                            >
                                <UploadCloud size={13} /> Upload Files
                            </button>
                        </div>
                        {attachments.length === 0 ? (
                            <EmptyState
                                icon={<Paperclip size={22} />}
                                title="No files attached yet"
                                text="Upload statements, insurance cards, call recordings, or any supporting document for this patient."
                            />
                        ) : (
                            <div className={styles.attachmentGrid}>
                                {attachments.map(att => (
                                    <div className={styles.attachmentCard} key={att.id}>
                                        <div className={styles.attachmentCardTop}>
                                            <div className={styles.attachmentCardIcon}>
                                                <FileText size={18} />
                                            </div>
                                            <div className={styles.attachmentCardName} title={att.file_name}>{att.file_name}</div>
                                        </div>
                                        <div className={styles.attachmentCardMeta}>
                                            <span>{att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : '—'}</span>
                                            <span>·</span>
                                            <span>{att.created_at ? new Date(att.created_at).toLocaleDateString() : '—'}</span>
                                        </div>
                                        <div className={styles.attachmentCardActions}>
                                            <button className={styles.downloadBtn} onClick={() => handleDownload(att.id)}>
                                                <Download size={12} /> Download
                                            </button>
                                            <button className={styles.deleteBtn} onClick={() => handleDeleteAttachment(att)}>
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* Extra Fields */}
                {activeTab === 4 && (
                    <>
                        <div className={styles.tabSectionHeader}>
                            <h3 className={styles.tabSectionTitle}>
                                <Database size={13} />
                                Imported Fields
                                {record.extra_fields && Object.keys(record.extra_fields).length > 0 && (
                                    <span className={styles.tabSectionCount}>{Object.keys(record.extra_fields).length}</span>
                                )}
                            </h3>
                        </div>
                        {!record.extra_fields || Object.keys(record.extra_fields).length === 0 ? (
                            <EmptyState
                                icon={<Database size={22} />}
                                title="No extra fields"
                                text="Fields from the import file that didn't map to standard properties will appear here."
                            />
                        ) : (
                            <div className={styles.extraFieldsGrid}>
                                {Object.entries(record.extra_fields).map(([k, v]) => (
                                    <div className={styles.extraField} key={k}>
                                        <span className={styles.extraFieldLabel}>{k}</span>
                                        <span className={styles.extraFieldValue}>{String(v ?? '—') || '—'}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <ConfirmModal
                isOpen={!!attachmentToDelete}
                onClose={() => !deletingAttachment && setAttachmentToDelete(null)}
                onConfirm={confirmDeleteAttachment}
                title="Delete Attachment"
                message={`Delete "${attachmentToDelete?.file_name ?? 'this file'}"? This cannot be undone.`}
                confirmText="Delete"
                type="danger"
                loading={deletingAttachment}
            />

            {showUploadModal && id && (
                <AttachmentUploadModal
                    serviceLogId={id}
                    onClose={() => setShowUploadModal(false)}
                    onUploaded={handleUploadedAttachments}
                />
            )}
        </div>
    );
};

const InfoCard: React.FC<{ title: string; icon: React.ReactNode; color: string; children: React.ReactNode }> = ({ title, icon, color, children }) => (
    <div className={styles.infoCard}>
        <div className={styles.infoCardHeader}>
            <span className={styles.infoCardIcon} style={{ background: color + '15', color }}>
                {icon}
            </span>
            <span className={styles.infoCardHeading}>{title}</span>
        </div>
        <div className={styles.infoCardBody}>{children}</div>
    </div>
);

const InfoRow: React.FC<{ label: string; value?: string | null; highlight?: boolean }> = ({ label, value, highlight }) => {
    if (!value) return null;
    return (
        <div className={styles.infoRow}>
            <span className={styles.infoLabel}>{label}</span>
            <span className={`${styles.infoValue} ${highlight ? styles.infoValueHighlight : ''}`}>{value}</span>
        </div>
    );
};

const MetricTile: React.FC<{ label: string; value: string; color: string; featured?: boolean }> = ({ label, value, color, featured }) => (
    <div
        className={`${styles.metricTile} ${featured ? styles.metricTileFeatured : ''}`}
        style={{ ['--metric-color' as any]: color }}
    >
        <span className={styles.metricLabel}>{label}</span>
        <span className={styles.metricValue}>{value}</span>
    </div>
);

const EmptyState: React.FC<{ icon: React.ReactNode; title: string; text?: string }> = ({ icon, title, text }) => (
    <div className={styles.emptyStateLarge}>
        <div className={styles.emptyStateIcon}>{icon}</div>
        <p className={styles.emptyStateTitle}>{title}</p>
        {text && <p className={styles.emptyStateText}>{text}</p>}
    </div>
);

const ArPill: React.FC<{ tone: 'amber' | 'blue' | 'violet'; label: string; value: string }> = ({ tone, label, value }) => {
    const palette: Record<string, { bg: string; fg: string; border: string }> = {
        amber: { bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
        blue: { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' },
        violet: { bg: '#f5f3ff', fg: '#6d28d9', border: '#ddd6fe' },
    };
    const c = palette[tone];
    return (
        <span className={styles.arPill} style={{ background: c.bg, color: c.fg, borderColor: c.border }}>
            <span className={styles.arPillLabel}>{label}</span>
            <span className={styles.arPillValue}>{value}</span>
        </span>
    );
};

export default PatientDetail;